import { useCallback, useEffect, useState } from 'react';
import '../styles/editable-record-table.css';
import '../styles/input-controls.css';
import LinkableTextArea from './LinkableTextArea.jsx';
import RecordDetailPopup from './RecordDetailPopup.jsx';
import { canEdit } from '../../../auth/permissions.js';

export default function ProgressReportEditPopup({
  open,
  initialRow,
  columns = [],
  onClose,
  onSave,
  saving = false,
  errorMessage = '',
  title = '進捗報告詳細',
}) {
  const [editingRow, setEditingRow] = useState(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open && initialRow) {
      setEditingRow({ ...initialRow });
    } else {
      setEditingRow(null);
    }
  }, [open, initialRow]);

  const updateField = useCallback((key, value) => {
    setEditingRow((prev) => (prev
      ? { ...prev, [key]: value }
      // updateField はフィールド描画（editingRow が非nullのときのみ）からのみ呼ばれるため到達不能
      : /* istanbul ignore next -- defensive guard: editingRow は常に非null */ prev));
  }, []);

  const handleSave = useCallback(() => {
    if (!onSave || !editingRow) return;

    const missingFields = columns
      .filter((c) => c.required && !c.readOnly)
      .filter((c) => {
        const val = editingRow[c.key];
        if (Array.isArray(val)) return val.length === 0;
        if (typeof val === 'boolean') return false;
        return !val && val !== 0;
      })
      .map((c) => c.label);
    if (missingFields.length > 0) {
      setValidationError(`以下の必須項目が未入力です：\n${missingFields.join('、')}`);
      return;
    }

    onSave(editingRow);
  }, [onSave, editingRow, columns]);

  const renderField = useCallback(
    (row, column) => {
      if (column.type === 'tags') {
        const raw = row[column.key] || '';
        const filled = raw.split(',').map((s) => s.trim()).filter(Boolean);
        const hasNewSlot = raw.trimEnd().endsWith(',');
        const items = hasNewSlot ? [...filled, ''] : filled;
        const opts = column.options || [''];

        const addItem = () => {
          const current = row[column.key] || '';
          const trimmed = current.replace(/,\s*$/, '');
          updateField(column.key, trimmed ? trimmed + ',' : ',');
        };

        if (items.length === 0) {
          return (
            <button
              type="button"
              onClick={addItem}
              style={{
                border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
                padding: '4px 12px', fontSize: '13px', color: '#6b7280', cursor: 'pointer',
              }}
            >
              ＋ 追加
            </button>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.map((item, i) => {
              const selectOpts = opts.includes(item) ? opts : [...opts, item];
              return (
                <div key={`${column.key}-${i}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <select
                    className="db-select-control"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[i] = e.target.value;
                      updateField(column.key, newItems.filter(Boolean).join(', '));
                    }}
                    style={{ flex: 1 }}
                  >
                    {selectOpts.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || '選択してください'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = items.filter((_, idx) => idx !== i);
                      updateField(column.key, newItems.join(', '));
                    }}
                    style={{
                      border: 'none', background: 'transparent', color: '#ef4444',
                      fontSize: '16px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                    }}
                    title="削除"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addItem}
              style={{
                border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
                padding: '3px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer',
                alignSelf: 'flex-start',
              }}
            >
              ＋ 追加
            </button>
          </div>
        );
      }

      if (column.type === 'select') {
        return (
          <select
            className="db-select-control"
            onChange={(e) => updateField(column.key, e.target.value)}
            value={row[column.key] || ''}
          >
            {(column.options || ['']).map((opt) => (
              <option key={opt || 'empty'} value={opt}>
                {opt || '選択してください'}
              </option>
            ))}
          </select>
        );
      }

      if (column.type === 'checkbox') {
        return (
          <input
            checked={Boolean(row[column.key])}
            onChange={(e) => updateField(column.key, e.target.checked)}
            type="checkbox"
          />
        );
      }

      if (['comment', 'pmoComment'].includes(column.key)) {
        return (
          <LinkableTextArea
            onChange={(next) => updateField(column.key, next)}
            value={row[column.key]}
          />
        );
      }

      if (column.type === 'date') {
        return (
          <input
            className="db-input-control"
            max="9999-12-31"
            onChange={(e) => updateField(column.key, e.target.value)}
            type="date"
            value={row[column.key] || ''}
          />
        );
      }

      return (
        <input
          className="db-input-control"
          onChange={(e) => updateField(column.key, e.target.value)}
          type="text"
          value={row[column.key] || ''}
        />
      );
    },
    [updateField],
  );

  const displayError = validationError || errorMessage;

  return (
    <>
      <RecordDetailPopup
        open={open && Boolean(editingRow)}
        title={title}
        columns={columns}
        row={editingRow}
        readOnly={!canEdit('ダッシュボード')}
        onClose={onClose}
        onSave={canEdit('ダッシュボード') ? handleSave : undefined}
        saving={saving}
        errorMessage={errorMessage}
        renderField={renderField}
      />
      {validationError && (
        <div className="db-error-modal-overlay" onClick={() => setValidationError('')} role="presentation">
          <div className="db-error-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="db-error-modal-icon">!</div>
            <p className="db-error-modal-message">{validationError}</p>
            <button className="db-error-modal-button" onClick={() => setValidationError('')} type="button">
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
